import { useEffect, useRef, useState } from "react";
import PageHero from "@/components/PageHero";
import api from "@/lib/api";
import { Shield, Search, AlertCircle, CheckCircle2, Award, Mail } from "lucide-react";

/**
 * Public /certificates — verification lookup ONLY.
 * Visitors enter an NKF-KYU-### / NKF-DAN-### number and get back that
 * single certificate's full record. No browsing, no filters, no name search
 * — those are admin-only.
 */
export default function Certificates() {
  const [cert, setCert] = useState(null);           // the matched cert
  const [input, setInput] = useState("");
  const [status, setStatus] = useState("idle");     // idle | searching | found | not-found | error
  const [errMsg, setErrMsg] = useState("");
  const abortRef = useRef(null);

  const normalized = (input || "").trim().toUpperCase();

  useEffect(() => {
    // reset display when input is empty / too short
    if (!normalized || normalized.length < 4) {
      setCert(null); setStatus("idle"); setErrMsg("");
      return;
    }
    const t = setTimeout(async () => {
      setStatus("searching");
      setErrMsg("");
      try {
        const r = await api.get(`/certificates/${encodeURIComponent(normalized)}`);
        setCert(r.data);
        setStatus("found");
      } catch (e) {
        setCert(null);
        if (e.response?.status === 404) {
          setStatus("not-found");
        } else {
          setStatus("error");
          setErrMsg(e.message || "Lookup failed");
        }
      }
    }, 400);
    return () => clearTimeout(t);
  }, [normalized]);

  return (
    <div data-testid="certificates-page">
      <PageHero
        eyebrow="Certificate Verification"
        title="Verify an NKF Bharat Belt Grading Certificate"
        subtitle="Enter the certificate number printed on your NKF belt grading certificate to confirm its authenticity and view the official record."
      />

      <section className="section-spacing nkf-container max-w-3xl">
        <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm" data-testid="verify-box">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-nkf-red" />
            <h2 className="font-display text-xl text-nkf-blue">Enter Certificate Number</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            Format: <span className="font-mono text-nkf-blue">NKF-KYU-###</span> or <span className="font-mono text-nkf-blue">NKF-DAN-###</span>.
          </p>
          <div className="mt-4 relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. NKF-DAN-215"
              data-testid="certificates-search"
              autoFocus
              className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-md text-base font-mono focus:outline-none focus:ring-2 focus:ring-nkf-blue/30 focus:border-nkf-blue"
            />
          </div>
          {status === "searching" && (
            <div className="mt-3 text-sm text-slate-500" data-testid="verify-status-searching">Looking up registry…</div>
          )}
        </div>

        {/* Result area */}
        <div className="mt-6">
          {status === "found" && cert && <CertResultCard c={cert} />}
          {status === "not-found" && (
            <div className="border border-dashed border-red-200 bg-red-50 rounded-md p-8 text-center" data-testid="verify-not-found">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <div className="font-display text-lg text-red-700">No certificate found</div>
              <p className="text-sm text-red-600 mt-1">
                We couldn't find a certificate with number <span className="font-mono font-semibold">{normalized}</span>.
                Please re-check the number on your physical certificate.
              </p>
            </div>
          )}
          {status === "error" && (
            <div className="border border-dashed border-amber-200 bg-amber-50 rounded-md p-6 text-center">
              <AlertCircle className="h-6 w-6 text-amber-500 mx-auto mb-2" />
              <div className="text-sm text-amber-700">{errMsg || "Lookup failed. Please try again."}</div>
            </div>
          )}
          {status === "idle" && (
            <div className="text-center text-slate-400 text-sm py-10">
              <Award className="h-7 w-7 mx-auto mb-2 text-slate-400" />
              Start typing a certificate number to verify.
            </div>
          )}
        </div>

        <div className="mt-12 p-6 md:p-8 bg-nkf-navy text-white rounded-md flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Mail className="h-7 w-7 text-nkf-gold shrink-0" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-nkf-gold">Official Registry</div>
              <div className="font-display text-2xl mt-1">Need a duplicate certificate or verification letter?</div>
              <div className="text-white/70 text-sm mt-1">Contact the federation secretariat with your certificate number.</div>
            </div>
          </div>
          <a href="/contact" className="bg-nkf-gold text-nkf-navy px-5 py-2.5 rounded text-sm uppercase tracking-widest font-bold hover:bg-white transition">Contact Federation</a>
        </div>
      </section>
    </div>
  );
}

function CertResultCard({ c }) {
  const isDan = c.kyu_or_dan === "dan";
  return (
    <article data-testid="verify-result" className={`bg-white border rounded-md overflow-hidden shadow-sm ${isDan ? "border-nkf-gold/50" : "border-slate-200"}`}>
      <div className={`px-5 py-3 flex items-center justify-between gap-3 ${isDan ? "bg-nkf-navy text-white" : "bg-slate-50"}`}>
        <div className="flex items-center gap-2 min-w-0">
          <CheckCircle2 className={`h-5 w-5 shrink-0 ${isDan ? "text-nkf-gold" : "text-emerald-600"}`} />
          <div className="min-w-0">
            <div className={`text-[10px] uppercase tracking-[0.3em] ${isDan ? "text-nkf-gold" : "text-nkf-red"}`}>Verified · Official Record</div>
            <div className={`font-display text-xl truncate ${isDan ? "text-white" : "text-nkf-blue"}`}>{c.athlete_name || "—"}</div>
          </div>
        </div>
        <BeltChip color={c.belt_color} rank={c.belt_rank} isDan={isDan} />
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
        <Row label="Certificate No." value={c.certificate_number} mono />
        {c.belt_rank && <Row label="Rank" value={c.belt_rank} />}
        {c.exam_year && <Row label="Year of Exam" value={c.exam_date || c.exam_year} />}
        {c.style && <Row label="Style" value={c.style} />}
        {c.format && <Row label="Format" value={c.format} />}
        {c.coach_name && <Row label="Examiner / Coach" value={c.coach_name} />}
        {c.academy && <Row label="Academy" value={c.academy} />}
        {c.district && <Row label="District" value={c.district} />}
        {c.state && <Row label="State" value={c.state} />}
        {c.dojo && <Row label="Dojo" value={c.dojo} />}
      </div>
    </article>
  );
}

function Row({ label, value, mono }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 last:border-b-0 py-1.5">
      <span className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold shrink-0 pt-0.5">{label}</span>
      <span className={`text-right text-slate-700 ${mono ? "font-mono text-xs" : ""}`}>{value}</span>
    </div>
  );
}

function BeltChip({ color, rank, isDan }) {
  const palette = {
    white: "bg-white text-slate-700 border-slate-300",
    yellow: "bg-yellow-200 text-yellow-900 border-yellow-400",
    orange: "bg-orange-200 text-orange-900 border-orange-400",
    green: "bg-emerald-200 text-emerald-900 border-emerald-400",
    blue: "bg-sky-200 text-sky-900 border-sky-400",
    purple: "bg-purple-200 text-purple-900 border-purple-400",
    brown: "bg-amber-200 text-amber-900 border-amber-500",
    red: "bg-red-200 text-red-900 border-red-400",
    black: "bg-slate-900 text-nkf-gold border-nkf-gold",
  };
  const cls = palette[color] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`shrink-0 inline-flex items-center text-[10px] uppercase tracking-widest font-bold border rounded-full px-2.5 py-1 ${cls}`}>
      {color ? color.toUpperCase() : (rank || "BELT")}
    </span>
  );
}
